import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  mapSurfaceCaptionLabelRecipe,
  mapSurfaceCaptionRecipe,
  mapSurfaceLayerRecipe,
  mapSurfaceOverlayRecipe,
  mapSurfaceRecipe,
} from '../recipes/map.recipe';
import { MapSkeleton } from './MapSkeleton';

export interface MapSurfaceProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The map itself: the app's Google map, with the library's pins on it. */
  children?: React.ReactNode;
  /** What floats along the top, clear of the notch: a header, the pills. */
  overlay?: React.ReactNode;
  /**
   * The map has not drawn yet. The skeleton covers it and says this, so a
   * half-loaded map is never seen.
   */
  loadingLabel?: string;
  /** A card in the gutter, rounded and ruled, rather than edge to edge. */
  framed?: boolean;
  /** What the map is, in a glass label at the bottom: "Truck map". */
  caption?: React.ReactNode;
}

/**
 * A full-bleed map with things floating over it, for a phone screen: the
 * calls map behind its list, the call detail behind its sheet.
 *
 *   <MapSurface
 *     overlay={<MobileHeader … />}
 *     loadingLabel={isLoaded ? undefined : 'Loading the map'}
 *   >
 *     <GoogleMap …>{pins}</GoogleMap>
 *   </MapSurface>
 *
 * A slot rather than a map. The apps draw maps with Google's, through
 * `@react-google-maps/api`, which needs an API key and a script loader that
 * belong to the app. Wrapping it here would pull both into every consumer of
 * the library. So the library owns what is around the map and on it (this,
 * MapSkeleton and the pins) and the app passes the map in.
 */
export const MapSurface = React.forwardRef<HTMLDivElement, MapSurfaceProps>(
  function MapSurface(
    {
      children,
      overlay,
      loadingLabel,
      framed = false,
      caption,
      className,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(mapSurfaceRecipe({ framed }), className)}
        {...props}
      >
        <div className={mapSurfaceLayerRecipe()}>{children}</div>
        {loadingLabel ? (
          <MapSkeleton label={loadingLabel} className="absolute inset-0" />
        ) : null}
        {overlay ? (
          <div className={mapSurfaceOverlayRecipe()}>{overlay}</div>
        ) : null}
        {caption ? (
          <div className={mapSurfaceCaptionRecipe()}>
            <span className={mapSurfaceCaptionLabelRecipe()}>{caption}</span>
          </div>
        ) : null}
      </div>
    );
  }
);
